import { auth } from './firebase';

const baseURL = 'https://apertai.uc.r.appspot.com';

export const ADMIN_EMAILS = ['teus.hcp@gmail.com'];

export function isAdminUser(email?: string | null): boolean {
  return !!email && ADMIN_EMAILS.includes(email.toLowerCase());
}

// Token sempre fresco (o accessToken do localStorage expira em ~1h)
async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('não autenticado');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const response = await fetch(`${baseURL}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((body as any).error || `HTTP ${response.status}`);
  }
  return body as T;
}

const json = (data: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(data),
});

// ---------- tipos ----------
export interface FleetCam {
  cam: number | string;
  state?: string;
  city?: string;
  court?: string;
  subcourt?: string;
}

export interface FleetCourt {
  slug: string;
  hostname?: string;
  identity?: { state?: string; city?: string; court?: string };
  cams?: FleetCam[];
  cameras?: number | string;
  services?: Record<string, string>;
  services_down?: string[];
  status: 'online' | 'degraded' | 'offline';
  cpu_pct?: number;
  mem_pct?: number;
  disk_pct?: number;
  temp_c?: number;
  throttled?: string;
  uptime_s?: number | string;
  queue_depth?: number | string;
  error_videos?: number | string;
  last_seen?: string | null;
  agent_version?: string;
  process_queue_version?: string;
  git_branch?: string;
  sponsors?: { file: string; slot: string }[];
  config?: { button_system?: 'gpio' | 'arduino' };
}

export interface FleetResponse {
  courts: FleetCourt[];
  aggregates: {
    total: number;
    offline_count: number;
    degraded_count: number;
    avg_cpu: number;
    avg_temp: number;
    total_queue: number;
  };
}

export interface BillingRow {
  rowIndex: number;
  nome: string;
  contato: string;
  valor: string;
  status: string;
  dataUltimoAviso: string;
  diaCobranca: string;
  desconto: string;
  // bloqueio por inadimplência (mesma planilha do bot)
  avisosEnviados?: number;
  faturasAbertas?: number;
  liberado?: boolean;
  // presente nas linhas de snapshot pagas com atraso (baixa retroativa)
  paid_late_at?: string;
}

export interface DailyReport {
  date: string;
  generated_at: string;
  trigger?: string;
  aggregates: FleetResponse['aggregates'];
  quadras_offline: { slug: string; court?: string; last_seen?: string }[];
  services_down: { slug: string; service: string }[];
  cpu_alerts: { slug: string; cpu_pct: number }[];
  temp_alerts: { slug: string; temp_c: number; throttled?: string }[];
  queue_backlogs: { slug: string; queue_depth: number }[];
  error_videos: { slug: string; error_videos: number }[];
  zero_video_courts: string[];
  videos_by_court: Record<string, number | null>;
  risk: { slug: string; court?: string; level: string; reasons: string[] }[];
}

export interface CommandStatus {
  id: string;
  type: string;
  status: 'pending' | 'running' | 'done' | 'error';
  result?: string;
}

export interface SponsorEntry {
  file: string;
  slot: string;
  previewUrl?: string | null;
}

// ---------- frota ----------
export const getFleet = () => request<FleetResponse>('/admin/fleet');
export const getFleetCourt = (slug: string) =>
  request<FleetCourt & { commands: CommandStatus[] }>(`/admin/fleet/${slug}`);
export const postCommand = (slug: string, type: string, params?: any) =>
  request<{ commandId: string }>(`/admin/fleet/${slug}/command`, json({ type, params }));
export const getCommandStatus = (slug: string, commandId: string) =>
  request<CommandStatus>(`/admin/fleet/${slug}/command/${commandId}`);
export const getVideosToday = (slug: string) =>
  request<{ total: number; by_subcourt: Record<string, number> }>(
    `/admin/fleet/${slug}/videos-today`);

// espera um comando concluir (polling 2s, timeout configurável)
export async function waitCommand(
  slug: string, commandId: string, timeoutMs = 120000,
): Promise<CommandStatus> {
  const start = Date.now();
  for (;;) {
    const cmd = await getCommandStatus(slug, commandId);
    if (cmd.status === 'done' || cmd.status === 'error') return cmd;
    if (Date.now() - start > timeoutMs) return cmd;
    await new Promise((r) => setTimeout(r, 2000));
  }
}

// ---------- relatório ----------
export const getReportToday = () => request<DailyReport>('/admin/report/today');
export const regenerateReport = () =>
  request<DailyReport>('/admin/report/generate', { method: 'POST' });

// ---------- pagamentos ----------
export const getBilling = () => request<BillingRow[]>('/admin/billing');
// competencia: baixa retroativa de um (string) ou vários (string[]) meses já
// fechados — abate a fatura e marca o snapshot daquele mês como pago.
export const postBillingAction = (
  rowIndex: number, action: string, value?: string, competencia?: string | string[],
) =>
  request<{ rowIndex: number }>(`/admin/billing/${rowIndex}`, json({ action, value, competencia }));

// histórico mensal (snapshot tirado todo dia 1 antes do reset do bot)
export interface BillingHistoryDoc {
  month: string;
  generated_at: string;
  rows: BillingRow[];
  totals: { recebido: number; inadimplente: number; manutencao: number; implantacao: number };
}
export const getBillingHistoryMonths = () => request<string[]>('/admin/billing/history');
export const getBillingHistoryMonth = (month: string) =>
  request<BillingHistoryDoc>(`/admin/billing/history/${month}`);

export function whatsappChargeLink(row: BillingRow): string {
  const valor = row.valor.toString().replace(/^R\$\s*/, '');
  const msg =
    `Olá, ${row.nome}! Passando para lembrar da mensalidade do Apertai ` +
    `no valor de R$ ${valor}. Qualquer dúvida estou à disposição!`;
  const phone = row.contato.replace(/\D/g, '');
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
}

// ---------- patrocínios ----------
export const getSponsors = (slug: string) =>
  request<{ city: string; sponsors: SponsorEntry[] }>(`/admin/sponsorship/${slug}`);

export async function addSponsor(slug: string, file: File) {
  const headers = await authHeaders();
  const form = new FormData();
  form.append('image', file);
  const response = await fetch(`${baseURL}/admin/sponsorship/${slug}`, {
    method: 'POST', headers, body: form,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((body as any).error || `HTTP ${response.status}`);
  return body as { sponsors: SponsorEntry[]; commands: { commandId: string }[] };
}

export const removeSponsor = (slug: string, file: string) =>
  request<{ sponsors: SponsorEntry[] }>(`/admin/sponsorship/${slug}/${encodeURIComponent(file)}`, {
    method: 'DELETE',
  });

// ---------- financeiro (Firestore) ----------
export interface FinanceEntry {
  id: string;
  kind: 'investimento' | 'despesa' | 'receita_manual' | 'retirada';
  cliente: string | null;
  description: string;
  amount: number;
  installments: number;
  startMonth: string;
}
export interface FinanceClient {
  nome: string;
  mensalidade: number;
  status: string | null;
  recebido_total: number;
  investido: number;
  despesas: number;
  vencido: number;
  a_vencer: number;
  lucro: number;
  gasto_medio_mensal: number;
  payback_meses_restantes: number | null;
  ja_se_pagou: boolean;
}
export interface FinanceSeriePoint {
  month: string;
  tipo: 'realizado' | 'projetado';
  receita: number;
  saidas: number;
  saldo: number;
  acumulado: number;
}
export interface MonthDetail {
  month: string;
  tipo: 'realizado' | 'projetado';
  receita_itens: { cliente: string; valor: number; tipo: 'realizado' | 'previsto' }[];
  saida_itens: { cliente: string; descricao: string; kind: string; valor: number; parcela: string | null }[];
  retirada_itens: { descricao: string; valor: number }[];
  total_receita: number;
  total_saidas: number;
  total_retiradas: number;
  saldo: number;
}

export interface FinanceOverview {
  mes_atual: string;
  global: {
    recebido_total: number;
    investido_total: number;
    despesas_total: number;
    saidas_vencidas: number;
    lucro_total: number;
    divida_futura: number;
    caixa_atual: number;
    retiradas_total: number;
    conta_nome: string;
    perda_implantacao: number;
    mensalidade_prevista: number;
    break_even_projetado: string;
  };
  serie: FinanceSeriePoint[];
  meses_detalhe: Record<string, MonthDetail>;
  clientes: FinanceClient[];
  aguardando_implantacao: {
    id: string | null; rowIndex: number | null; nome: string; branch: string | null;
    valor_previsto: number; investido: number;
    prevista_em: string | null; atraso_dias: number; perda_estimada: number;
  }[];
  receita_por_mes: Record<string, Record<string, number>>;
  saidas_por_mes: Record<string, Record<string, number>>;
}
export const getFinanceOverview = () => request<FinanceOverview>('/admin/finance/overview');
export const getFinanceEntries = () => request<FinanceEntry[]>('/admin/finance/entries');
export const addFinanceEntry = (entry: Omit<FinanceEntry, 'id'>) =>
  request<{ id: string }>('/admin/finance/entries', json(entry));
export const deleteFinanceEntry = (id: string) =>
  request<{ id: string }>(`/admin/finance/entries/${id}`, { method: 'DELETE' });
export const setImplantacaoPrevista = (nome: string, prevista_em: string) =>
  request<{ nome: string }>('/admin/finance/implantacao-prevista', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nome, prevista_em }),
  });
export const setFinanceConfig = (config: { account_name: string }) =>
  request<{ account_name: string }>('/admin/finance/config', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });

// ---------- config da quadra (ex: sistema de botao Arduino) ----------
export const patchCourtConfig = (slug: string, config: { button_system: 'gpio' | 'arduino' }) =>
  request<{ slug: string }>(`/admin/fleet/${slug}/config`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });

// ---------- criar arena nova ----------
export const getArenaBranches = () => request<string[]>('/admin/arenas/branches');

export interface CreateArenaParams {
  branch: string;
  branchMode?: 'create' | 'existing';
  baseBranch?: string;
  state?: string;
  cityBucket: string;
  cityDisplay?: string;
  court: string;
  subcourts: string[];
  status?: 'rodando' | 'a_implantar';
  billing?: { nome?: string; contato?: string; valor?: string; diaCobranca?: number };
}
export const createArena = (params: CreateArenaParams) =>
  request<{ branch: string; steps: string[]; next: string }>('/admin/arenas', json(params));

export const activatePendingArena = (id: string) =>
  request<{ id: string; nome: string; steps: string[] }>(
    `/admin/arenas/pending/${id}/activate`, { method: 'POST' });

// ---------- clientes (Firestore site) ----------
export const patchClient = (id: string, updates: any) =>
  request<{ id: string }>(`/admin/clients/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
